import { BusInfo, BusRouteInfo, BusSelectorMappedResponse } from '@/scopes/prices/domain/transports/types'
import { BusItem, BusRoute, BusSelectorApiRawResponse } from '@/scopes/prices/infrastructure/transport/bus/types'
import { mapPriceBreakdown } from '@/scopes/prices/infrastructure/transport/mapPriceBreakdown'
import { mapBusRouteTripSummary, mapTripSummary } from '@/scopes/prices/infrastructure/transport/mapTripSummary'
import { TransportType } from '@/scopes/prices/infrastructure/transport/urlBuilder/transportSelectorUrlBuilder'

export interface BusSelectorResponseMapper {
  map: (busSelectorResponse: BusSelectorApiRawResponse) => BusSelectorMappedResponse
}

export function createBusSelectorResponseMapper(): BusSelectorResponseMapper {
  return {
    map: (busSelectorResponse: BusSelectorApiRawResponse) => {
      function mapBusItem(responseBus: BusItem) {
        return {
          id: responseBus.busId,
          departureDate: {
            time: responseBus.departureDate.formattedTime,
            fullDate: responseBus.departureDate.formattedDate,
            rawDate: responseBus.departureDate.raw,
          },
          arrivalDate: {
            time: responseBus.arrivalDate.formattedTime,
            fullDate: responseBus.arrivalDate.formattedDate,
            rawDate: responseBus.arrivalDate.raw,
          },
          departureCityName: responseBus.departureCityName,
          arrivalCityName: responseBus.arrivalCityName,
          departureCode: responseBus.departureCode,
          departureStationName: responseBus.departureStationName,
          arrivalCode: responseBus.arrivalCode,
          arrivalStationName: responseBus.arrivalStationName,
          averagePrice: responseBus.priceDiff.raw ?? 0,
          totalPrice: responseBus.priceDiffPerPackage.raw ?? 0,
        } as BusInfo
      }

      const inboundBuses = busSelectorResponse.inboundBuses.map(mapBusItem)
      const outboundBuses = busSelectorResponse.outboundBuses.map(mapBusItem)
      const tripSummary = busSelectorResponse.tripSummary?.map(mapTripSummary)

      function mapRouteInfo(route: BusRoute): BusRouteInfo {
        return {
          outboundBusId: route.outboundBusId,
          inboundBusId: route.inboundBusId,
          price: route.price,
          priceBreakdown: mapPriceBreakdown(route.priceBreakdown, route.price),
          tripSummary: mapBusRouteTripSummary(route.tripSummary),
        } as BusRouteInfo
      }

      function mapRoutes() {
        const grouped: Record<string, Record<string, BusRouteInfo>> = busSelectorResponse.routes.reduce(
          (acc, route) => {
            const outbound = route.outboundBusId
            const inbound = route.inboundBusId
            if (!acc[outbound])
              acc[outbound] = {}
            acc[outbound][inbound] = mapRouteInfo(route)
            return acc
          },
          {} as Record<string, Record<string, BusRouteInfo>>,
        )

        return grouped
      }

      const routes: Record<string, Record<string, BusRouteInfo>> = mapRoutes()

      return {
        transportType: TransportType.Bus,
        tripSummary,
        inboundBuses,
        outboundBuses,
        routes,
      } as BusSelectorMappedResponse
    },
  }
}
